import { useEffect, useMemo } from "react";
import { useParams, useNavigate, Outlet } from "react-router-dom";
import { Header, Loading, Button } from "@core/components";
import ProfileSettings from "@core/components/ProfileMenu";
import { useWorkspaceContext, useSidebarContext } from "@core/context";
import { cn } from "@core/utils";
import { useGetProject } from "../hooks";
import { useTranslate } from "../hooks/useTranslate";
import { ProjectContext } from "../context";
import { ProjectSidebar } from "../components";

// Shell rendered by ProjectGuard after all checks pass
const ProjectWorkspaceShell = () => {
  const { collapsed } = useSidebarContext();
  return (
    <div className="hidden md:block min-h-screen w-full">
      <ProjectSidebar />
      <div
        className={cn(
          "min-w-0 transition-[margin] duration-200 ease-in-out",
          collapsed ? "ml-[4.3125rem]" : "md:ml-[13rem] lg:ml-[17.125rem]",
        )}
      >
        <Header>
          <ProfileSettings />
        </Header>
        <main className="mx-auto w-full flex flex-col gap-2 max-w-[2000px] px-5 2xl:px-10 py-3">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const ProjectGuard = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { workspaceId } = useWorkspaceContext();
  const navigate = useNavigate();
  const { t } = useTranslate();

  const { data: project, isLoading, isError } = useGetProject(projectId ?? "");

  const contextValue = useMemo(
    () => ({
      project: project!,
      projectId: project?.id ?? "",
      role: project?.role ?? ("member" as const),
      rootFolderId: project?.folder_id ?? "",
    }),
    [project],
  );

  useEffect(() => {
    if (!projectId) return;
    if (isLoading) return;
    if (isError) return;
    if (!project || !project.is_active) {
      navigate(`/workspace/${workspaceId}/project-portal`, { replace: true });
    }
  }, [isLoading, isError, project, projectId, navigate, workspaceId]);

  if (!projectId) return <Loading isLoading />;
  if (isLoading) return <Loading isLoading />;

  if (isError) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center h-full gap-3 p-8",
        )}
      >
        <p className="text-sm text-center text-muted-foreground">
          {t("load-error")}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            navigate(`/workspace/${workspaceId}/project-portal`, {
              replace: true,
            })
          }
        >
          {t("back-to-portal")}
        </Button>
      </div>
    );
  }

  if (!project || !project.is_active) {
    return null;
  }

  return (
    <ProjectContext.Provider value={contextValue}>
      <ProjectWorkspaceShell />
    </ProjectContext.Provider>
  );
};
